import type {
  ApolloCache,
  GraphQlDocument,
  GraphQlError,
  UseMutationOptions,
} from '@sail/data';

import type {V1ContextInput} from '../../../schema/types';
import type {V1ApolloCache, V1MutationOptions} from '../types';

import mergeContextVariables from '../../shared/utils/mergeContextVariables';

export default function wrapOptionsWithCache<
  TData,
  TVariables extends {v1Context: V1ContextInput},
  TPermissions,
  THookVariables extends Partial<TVariables>,
>(
  options: V1MutationOptions<
    GraphQlDocument<TData, TVariables, TPermissions>,
    THookVariables
  >,
  v1Context: V1ContextInput,
): UseMutationOptions<
  GraphQlDocument<TData, TVariables, TPermissions>,
  THookVariables
> {
  if (!options.update) {
    return options as UseMutationOptions<
      GraphQlDocument<TData, TVariables, TPermissions>,
      THookVariables
    >;
  }

  const originalUpdate = options.update;

  return {
    ...(options as UseMutationOptions<
      GraphQlDocument<TData, TVariables, TPermissions>,
      THookVariables
    >),

    update: (
      cache: ApolloCache,
      result: {
        data?: TData | null | undefined;
        errors?: readonly GraphQlError[] | undefined;
      },
    ) => {
      return originalUpdate(wrapCache(cache, v1Context), result);
    },
  };
}

export function wrapCache(
  cache: ApolloCache,
  v1Context: V1ContextInput,
): V1ApolloCache {
  return {
    evict: (object) => cache.evict(object),
    gc: () => cache.gc(),
    identify: (object) => cache.identify(object),
    modify: (options) => cache.modify(options),

    readQuery: ({query, variables}) => {
      return cache.readQuery({
        query,
        variables: {
          ...variables,
          v1Context: mergeContextVariables(v1Context, variables?.v1Context),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
    },

    writeQuery: ({query, variables, data}) => {
      return cache.writeQuery({
        query,
        variables: {
          ...variables,
          v1Context: mergeContextVariables(v1Context, variables?.v1Context),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        data,
      });
    },
  };
}
